<template>
  <div>
    <label
      class="input__label margin__b--6"
      v-if="translate(title)"
      for="custom_amount"
    >
      {{ translate(title) }} <span v-if="block.is_required">*</span>
    </label>

    <div
      class="margin__b--12"
      v-if="activeCurrencies.length > 1"
    >
      <input-select-currency
        v-model="currency"
        v-if="show_currency"
      />

      <button
        class="link__color--sec"
        @click="show_currency = true"
        v-else
      >
        {{ t('literal.change_currency') }}
      </button>
    </div>

    <fieldset class="amounts">
      <legend class="visually-hidden">
        {{ t('literal.choose_amount') }}
      </legend>

      <div
        class="amount"
        v-for="amount in amounts"
        :key="amount"
      >
        <input
          type="radio"
          class="amount__input visually-hidden"
          v-model="domiciliationAmount"
          :value="amount"
          :id="`${id}_${amount}`"
        >

        <label
          class="amount__label d-block padding--12 align--center"
          :for="`${id}_${amount}`"
        >
          <app-money :amount="amount" />
        </label>
      </div>
    </fieldset>

    <div
      class="margin__t--12"
      v-if="isCustomAmountFieldRequired"
    >
      <label
        class="input__label margin__b--6"
        for="custom_amount"
      >
        {{ translate(label) }}
      </label>

      <input-amount
        id="custom_amount"
        v-model.number="customAmount"
        :v="validator"
        :class="{ 'color--vlg': !customAmount }"
        placeholder="0"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, inject, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useCurrencyStore } from '@/stores/CurrencyStore'
import { useModule } from '@/composables/app/useModule'
import { useMoney } from '@/composables/app/useMoney'
import { useProcedureBlockValidator } from '@/composables/modules/procedures'
import { get as _get, set as _set } from 'lodash'
import { useI18n } from '@/vendors/i18n'

const InputAmount = defineAsyncComponent(() => import('&/atoms/InputAmount'))
const InputSelectCurrency = defineAsyncComponent(() => import('&/atoms/InputSelectCurrency'))
const AppMoney = defineAsyncComponent(() => import('&/atoms/AppMoney'))

const { block } = defineProps({
  block: { type: Object, required: true }
})

const procedure_execution = inject('procedure_execution')

const timeout = ref()
const show_currency = ref(false)

const { t, translate } = useI18n()
const { getAmount } = useMoney()
const { activeCurrencies } = useCurrencyStore()
const { currency } = storeToRefs(useCurrencyStore())
const { id, attributes, model } = useModule({ component: block, parent: procedure_execution })

const domiciliationAmount = computed({
  get: () => parseInt(model.value) || 0,
  set: amount => model.value = parseInt(amount) || 0
})

const { validator } = useProcedureBlockValidator({ validations: block.mandatory, state: domiciliationAmount })

const title = computed(() => _get(attributes.value, 'title', {}))
const label = computed(() => _get(attributes.value, 'label', {}))
const amounts = computed(() => _get(attributes.value, 'amounts', []).map(amount => getAmount({ amount: amount.value })))
const defaultAmount = computed(() => getAmount({ amount: _get(attributes.value, 'highlighted_amount', _get(amounts.value, '0', 0)) }))
const isCustomAmountFieldRequired = computed(() => _get(attributes.value, 'custom_amount_field_required', true))

const customAmount = computed({
  get: () => domiciliationAmount.value !== 0 && !amounts.value.includes(domiciliationAmount.value) ? domiciliationAmount.value : null,
  set: value => {
    clearTimeout(timeout.value)

    timeout.value = setTimeout(() => domiciliationAmount.value = value, 500)
  }
})

watch(currency, () => _set(procedure_execution.value, 'informations.manual_domiciliation.currency', currency.value), { immediate: true })

if (!domiciliationAmount.value) domiciliationAmount.value = defaultAmount.value
</script>

<style lang="scss" scoped>
.amounts {
  border: 1px solid $dw;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;

  @include mq(md) {
    flex-direction: row;
  }
}

.amount {
  flex-grow: 1;
  position: relative;

  &__input {
    &:focus+.amount__label {
      border-color: $dg;
    }

    &:checked+.amount__label {
      cursor: default;
      background-color: $color--primary;
      border-color: $color--primary;
      color: set-contrast($color--primary);
    }

    &:disabled+.amount__label {
      background: transparent;
      color: $black;
      border-color: transparent;
    }
  }

  &__label {
    color: $lg;
    border-radius: 6px;
    width: 100%;
    user-select: none;
    white-space: nowrap;
    transition: all .3s;
    cursor: pointer;
    border: 1px solid transparent;

    &:hover {
      background-color: tint($color--primary, 95%, 95%);
      border-color: tint($color--primary, 95%, 95%);
      color: $black;
    }
  }
}
</style>
